// ============================================================================
// Paying a creator through Trolley, from the dashboard
// ============================================================================
// The other half of recording a payout. recordHirePayment (pay-hire.ts) writes
// down a payment ops already made in Trolley's dashboard; this SENDS one, via
// the trolley-creator-payout edge function (creator-app/supabase/functions).
//
// Nothing here decides anything. The edge function re-resolves the recipient,
// re-runs the payout rules, and the database holds the one-live-payout-per-hire
// lock, so this module is only transport + the words ops sees.
//
// A sent payout does NOT mark the hire paid. The hire stays unpaid while
// Trolley processes it and flips when Trolley reports the payment 'processed'
// (trolley-webhook). That is deliberate: marking a hire paid notifies the
// creator, and a Trolley payment can still fail or bounce.
// ============================================================================

import { supabase } from './supabase'
import type { TrolleyPayoutLike } from './payouts'

export type TrolleyPayoutStatus =
  | 'reserved' | 'submitted' | 'processed' | 'failed' | 'returned' | 'cancelled' | 'needs_reconciliation'

export interface TrolleyPayout extends TrolleyPayoutLike {
  id: string
  hire_id: string
  status: TrolleyPayoutStatus
  amount_cents: number
  mode: 'full' | 'partial' | null
  trolley_batch_id: string | null
  trolley_payment_id: string | null
  trolley_status: string | null
  error_message: string | null
  notes: string | null
  initiated_by_email: string
  cancelled_by_email: string | null
  created_at: string
  submitted_at: string | null
  processed_at: string | null
  failed_at: string | null
  returned_at: string | null
  cancelled_at: string | null
  last_synced_at: string | null
  /** Set when this payout went out in a weekly payout batch. */
  payout_batch_id: string | null
}

/** Columns to embed on a cr_hires select: `cr_hire_payouts(${TROLLEY_PAYOUT_COLUMNS})`. */
export const TROLLEY_PAYOUT_COLUMNS =
  'id, hire_id, status, amount_cents, mode, trolley_batch_id, trolley_payment_id, trolley_status, error_message, notes, ' +
  'initiated_by_email, cancelled_by_email, created_at, submitted_at, processed_at, failed_at, returned_at, cancelled_at, last_synced_at, ' +
  'payout_batch_id'

export interface TrolleyRecipient {
  id: string
  name: string | null
  email: string | null
  status: string | null
  complianceStatus: string | null
  payoutMethod: string | null
  enabledAccounts: number
  blocker: string | null
}

export interface TrolleyPreview {
  can_send: boolean
  decision: {
    mode: 'full' | 'partial' | null
    deliveredVideos: number
    requiredVideos: number
    pendingVideos: number
    earnedCents: number
    eligible: boolean
    blockedReason: string | null
  }
  recipient: TrolleyRecipient | null
  /** Why this creator can't be paid through Trolley right now; null when they can. */
  recipient_problem: string | null
  live_payout: TrolleyPayout | null
  /** Set when the hire is a line in the current DRAFT weekly batch. Doesn't
   *  block paying it here — the batch skips it when it's sent. */
  draft_batch?: { batch_id: string; amount_cents: number } | null
}

export interface TrolleySendResult {
  ok: boolean
  replayed?: boolean
  needs_reconciliation?: boolean
  payout: TrolleyPayout | null
  batch_status?: string | null
  message: string
}

export interface TrolleySyncResult {
  ok: boolean
  payout: TrolleyPayout
  batch_status: string | null
  cancellable: boolean
  note: string | null
}

/** Carries the payout row when the function failed AFTER creating one, so the
 *  UI can show the attempt's state instead of just an error string. */
export class TrolleyPayoutError extends Error {
  constructor(message: string, public payout: TrolleyPayout | null = null) {
    super(message)
  }
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('trolley-creator-payout', { body })
  if (error) {
    // FunctionsHttpError keeps the Response on .context; the function always
    // answers JSON with { error } or { message, payout }.
    let payload: any = null
    try { payload = await error.context?.json?.() } catch { /* not JSON */ }
    throw new TrolleyPayoutError(payload?.error ?? payload?.message ?? error.message ?? 'Trolley payout request failed', payload?.payout ?? null)
  }
  return data as T
}

export const previewTrolleyPayout = (hireId: string) =>
  call<TrolleyPreview>({ action: 'preview', hire_id: hireId })

export const sendTrolleyPayout = (input: {
  hireId: string
  amountCents: number
  recipientId: string
  idempotencyKey: string
  notes?: string
}) => call<TrolleySendResult>({
  action: 'send',
  hire_id: input.hireId,
  amount_cents: input.amountCents,
  recipient_id: input.recipientId,
  idempotency_key: input.idempotencyKey,
  notes: input.notes?.trim() || undefined,
})

export const syncTrolleyPayout = (payoutId: string) =>
  call<TrolleySyncResult>({ action: 'sync', payout_id: payoutId })

export const cancelTrolleyPayout = (payoutId: string, reason?: string) =>
  call<{ ok: boolean; payout: TrolleyPayout }>({ action: 'cancel', payout_id: payoutId, reason })

/** One key per opened form: a double-click or a retried request collapses into
 *  the same attempt, a deliberate second payout later gets its own. */
export function newTrolleyIdempotencyKey(hireId: string): string {
  return `trolley_payout:${hireId}:${crypto.randomUUID()}`
}

/** Most recent attempt first — what the status card shows. */
export function latestTrolleyPayout(payouts: TrolleyPayout[] | null | undefined): TrolleyPayout | null {
  if (!payouts?.length) return null
  return [...payouts].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
}

export type PayoutTone = 'blue' | 'amber' | 'emerald' | 'red' | 'slate'

export function trolleyStatusLabel(p: Pick<TrolleyPayout, 'status' | 'trolley_status'>): { label: string; tone: PayoutTone; detail: string } {
  switch (p.status) {
    case 'reserved':
      return { label: 'Sending', tone: 'blue', detail: 'The payout is being handed to Trolley.' }
    case 'submitted':
      return {
        label: 'Processing in Trolley',
        tone: 'blue',
        detail: 'Trolley accepted the payout. The hire is marked paid automatically once Trolley confirms delivery — bank transfers can take a few days.',
      }
    case 'processed':
      return { label: 'Paid via Trolley', tone: 'emerald', detail: 'Trolley delivered the payment.' }
    case 'failed':
      return { label: 'Failed', tone: 'red', detail: 'No money reached the creator. The hire can be paid again.' }
    case 'returned':
      return {
        label: 'Returned',
        tone: 'red',
        detail: 'The payment bounced back to BMC (usually bad bank details) and the hire is unpaid again. Ask the creator to update their payout method in the app, then pay again.',
      }
    case 'cancelled':
      return { label: 'Cancelled', tone: 'slate', detail: 'Cancelled before any money moved.' }
    case 'needs_reconciliation':
      return {
        label: 'Needs sync',
        tone: 'amber',
        detail: 'We lost contact with Trolley mid-send, so it is unknown whether the payment was created. Sync checks Trolley; nothing else can be paid on this hire until it is resolved.',
      }
  }
}

// ============================================================================
// Weekly payout batches (trolley-payout-batch edge function)
// ============================================================================
// A draft lives only in the database; the Trolley batch is created when an
// admin confirms. Reads go straight to the tables (admin RLS); every write is
// the edge function's.

export type PayoutBatchStatus = 'draft' | 'processing' | 'sent' | 'needs_reconciliation'

export interface PayoutBatch {
  id: string
  status: PayoutBatchStatus
  attempt: number
  trolley_batch_id: string | null
  trolley_status: string | null
  total_cents: number | null
  payment_count: number | null
  error_message: string | null
  created_by_email: string
  confirmed_by_email: string | null
  confirmed_at: string | null
  sent_at: string | null
  last_built_at: string | null
  last_synced_at: string | null
  created_at: string
}

export interface PayoutBatchItem {
  id: string
  batch_id: string
  hire_id: string
  state: 'included' | 'removed' | 'blocked'
  amount_cents: number
  earned_cents: number
  reason: string | null
  warning: string | null
  payout_id: string | null
}

async function callBatch<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('trolley-payout-batch', { body })
  if (error) {
    let payload: any = null
    try { payload = await error.context?.json?.() } catch { /* not JSON */ }
    throw new Error(payload?.error ?? payload?.message ?? error.message ?? 'Payout batch request failed')
  }
  // confirm answers 202 (needs reconciliation) and 422 with { ok:false, message }
  // through the error branch above; a 2xx { ok:false } still needs surfacing.
  if (data && data.ok === false && data.message) throw new Error(data.message)
  return data as T
}

export const payoutBatchOverview = () => callBatch<{ can_send: boolean }>({ action: 'overview' })

export const buildPayoutBatch = () =>
  callBatch<{ ok: boolean; batch_id: string; included: number; blocked: number }>({ action: 'build' })

export const setPayoutBatchItem = (input: { itemId: string; state: 'included' | 'removed'; amountCents?: number }) =>
  callBatch<{ ok: boolean; item: PayoutBatchItem }>({
    action: 'set_item',
    item_id: input.itemId,
    state: input.state,
    amount_cents: input.amountCents,
  })

export const confirmPayoutBatch = (batchId: string) =>
  callBatch<{ ok: boolean; sent: number; total_cents: number; skipped: { hire_id: string; reason: string }[]; message: string }>({
    action: 'confirm',
    batch_id: batchId,
  })

export const syncPayoutBatch = (batchId: string) =>
  callBatch<{ ok: boolean; batch: PayoutBatch; cancellable: boolean; note: string | null }>({ action: 'sync', batch_id: batchId })

export const cancelPayoutBatch = (batchId: string) =>
  callBatch<{ ok: boolean; batch: PayoutBatch }>({ action: 'cancel', batch_id: batchId })

export function payoutBatchStatusLabel(status: PayoutBatchStatus): { label: string; tone: PayoutTone } {
  switch (status) {
    case 'draft':                return { label: 'Draft — not sent', tone: 'slate' }
    case 'processing':           return { label: 'Sending to Trolley', tone: 'blue' }
    case 'sent':                 return { label: 'Sent to Trolley', tone: 'emerald' }
    case 'needs_reconciliation': return { label: 'Needs sync', tone: 'amber' }
  }
}
